import { stablemintWithdrawErrorMessage } from "@/lib/api/messages";
import { withCurrency } from "@/lib/format/amount";

/* What the platform's realtime stream says about money, as the toasts the
   whitelabel shows for it (batman's _V2/Layout + react-sdk-v2's
   messageSchemas and useTransactionToasts), text for text.

   Two topics carry it. `accounts:{id}:transactions` sends a `transaction`
   message per stage of a transfer — pending, then success or error, under one
   id. `accounts:{id}:notifications` sends an `account-notification` for the
   steps a transfer has no stage for: a withdrawal held for review, approved or
   rejected, and everything Stablemint reports about a fiat deposit or
   withdrawal.

   Pure on purpose: the shell feeds messages in and applies what comes out, so
   every string here is unit-tested without a socket. */

export type TransactionMessage = {
  id: string;
  kind?: string;
  type?: string;
  status?: string;
};

/* A transfer's stage, as the whitelabel's showPendingToast / showSuccessToast
   / showErrorToast take it. */
export type TransactionToast =
  | { stage: "pending"; id: string; message: string }
  | { stage: "success" | "error"; id: string; message: string };

export const transactionToast = ({ id, kind, type, status }: TransactionMessage): TransactionToast | null => {
  switch (kind) {
    case "transfer": {
      const typeText = type === "credit" ? "Deposit" : "Withdrawal";

      if (status === "success") return { stage: "success", id, message: `${typeText} successful` };
      if (status === "pending") return { stage: "pending", id, message: `${typeText} processing...` };
      if (status === "error") {
        return { stage: "error", id, message: `${typeText} failed, retry if refunded, or contact support.` };
      }

      return { stage: "error", id, message: `${typeText} failed.` };
    }
    case "rakeback-claim":
      return status === "success" ? { stage: "success", id, message: "Rakeback successfully claimed" } : null;
    case "reward-claim":
      return status === "success" ? { stage: "success", id, message: "Reward successfully claimed" } : null;
    default:
      return null;
  }
};

export type AccountNotification = {
  type: string;
  data?: Record<string, unknown>;
};

export type NotificationToast = {
  tone: "success" | "info" | "error";
  title: string;
  /* Replaces a showing toast with the same key, as sonner's `id` does. */
  key: string;
};

export type NotificationEffect = NotificationToast | { kycRequired: true };

/* The whitelabel shows Stablemint's notifications for eight seconds — the
   template's own toast duration, so no override is needed. */

const text = (value: unknown) => (typeof value === "string" ? value : typeof value === "number" ? String(value) : "");

/* formatUnitsWithDecimals: base units to at most two decimals, trailing zeros
   dropped, thousands grouped. */
export const formatUnitsWithDecimals = (value: string, decimals: number) => {
  if (!/^-?\d+$/.test(value)) {
    return "Invalid Number";
  }

  const negative = value.startsWith("-");
  const display = (negative ? value.slice(1) : value).padStart(decimals, "0");
  const whole = display.slice(0, display.length - decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = display.slice(display.length - decimals).replace(/0+$/, "").slice(0, 2);

  return `${negative ? "-" : ""}${whole || "0"}${fraction ? `.${fraction}` : ""}`;
};

/* formatUSDTokenAsDollarAmount: the dollar sign for the two dollar tokens the
   whitelabel names, the ticker after the figure for anything else. */
export const formatUSDTokenAsDollarAmount = ({ amount, currency }: { amount: string; currency: string }) =>
  currency === "USDC" || currency === "USDSM" ? withCurrency(amount, currency) : `${amount} ${currency}`;

export const notificationEffect = (notification: AccountNotification): NotificationEffect | null => {
  const data = notification.data ?? {};
  const fiat = `${text(data.amount)} ${text(data.currency)}`;
  const key = notification.type;

  /* An on-chain withdrawal's review, approval or rejection, keyed by the
     transfer so two withdrawals in flight do not replace each other. */
  const withdrawal = () => {
    const decimals = typeof data.decimals === "number" ? data.decimals : 0;

    return formatUSDTokenAsDollarAmount({
      amount: formatUnitsWithDecimals(text(data.amount), decimals),
      currency: text(data.symbol),
    });
  };
  const withdrawalKey = `${notification.type}:${text(data.transferTransactionId)}`;

  switch (notification.type) {
    /* Bank deposit, with the deprecated customer.deposit names. */
    case "stablemint:bank.deposit:pending":
    case "stablemint:customer.deposit:pending":
      return { tone: "info", key, title: `Awaiting payment of ${fiat}` };
    case "stablemint:bank.deposit:completed":
    case "stablemint:customer.deposit:completed":
      return { tone: "success", key, title: `Deposit of ${fiat} received` };
    case "stablemint:bank.deposit:cancelled":
    case "stablemint:customer.deposit:cancelled":
      return { tone: "error", key, title: `Deposit of ${fiat} was cancelled` };
    case "stablemint:bank.deposit:expired":
      return { tone: "error", key, title: `Deposit of ${fiat} expired` };

    /* Bank transfer into the entry wallet, with the deprecated names. */
    case "stablemint:bank.transfer:pending":
    case "stablemint:customer.transfer:pending":
      return { tone: "info", key, title: `Depositing ${fiat} to your entry wallet` };
    case "stablemint:bank.transfer:completed":
    case "stablemint:customer.transfer:completed":
      return { tone: "success", key, title: `${fiat} deposited to your entry wallet` };

    /* Card deposit. */
    case "stablemint:card.deposit:completed":
      return { tone: "success", key, title: `Card deposit of ${fiat} received` };
    case "stablemint:card.deposit:expired":
      return { tone: "error", key, title: `Card deposit of ${fiat} expired` };
    case "stablemint:card.deposit:failed":
      return { tone: "error", key, title: `Card deposit of ${fiat} failed` };

    /* Fiat withdrawal. A KYC refusal opens the verification prompt instead. */
    case "stablemint:withdrawal:error":
      if (text(data.reason) === "KYC_REQUIRED") {
        return { kycRequired: true };
      }

      return { tone: "error", key, title: stablemintWithdrawErrorMessage(text(data.reason)) };
    case "stablemint:withdrawal:review":
      return { tone: "info", key, title: "Withdrawal is under review" };
    case "stablemint:withdrawal:processing":
      return { tone: "info", key, title: "Withdrawal is being processed" };
    case "stablemint:withdrawal:rejected":
      return { tone: "error", key, title: "Withdrawal was rejected, please contact support." };
    case "stablemint:bank.withdrawal:pending":
      return { tone: "info", key, title: `Withdrawal of ${fiat} is being processed` };

    /* On-chain withdrawal approvals. */
    case "withdrawal:review":
      return { tone: "info", key: withdrawalKey, title: `Withdrawal of ${withdrawal()} is under review` };
    case "withdrawal:approved":
      return {
        tone: "success",
        key: withdrawalKey,
        title: `Withdrawal of ${withdrawal()} approved and is being processed`,
      };
    case "withdrawal:rejected":
      return {
        tone: "error",
        key: withdrawalKey,
        title: `Withdrawal of ${withdrawal()} was rejected, please contact support.`,
      };

    default:
      return null;
  }
};

/* useTransactionToasts' bookkeeping. A pending toast stays until its
   transfer settles: one raised for a known transaction id is replaced by that
   id's next stage; one raised before any id is known — "Deposit pending..."
   right after a wallet payment — is cleared by the next settled transfer that
   has no pending toast of its own, oldest first. */
export type ToastSink = {
  push: (toast: { tone: "success" | "info" | "error"; title: string; key?: string; duration?: number }) => void;
  dismiss: (key: string) => void;
};

export const createTransactionToasts = (sink: ToastSink) => {
  const pendingById = new Map<string, string>();
  let unknown: string[] = [];
  let next = 0;

  const settle = (transactionId?: string) => {
    if (transactionId && pendingById.has(transactionId)) {
      sink.dismiss(pendingById.get(transactionId) as string);
      pendingById.delete(transactionId);
      return;
    }

    const [first, ...rest] = unknown;
    unknown = rest;
    if (first) sink.dismiss(first);
  };

  return {
    pending: (message: string, transactionId?: string) => {
      if (transactionId) settle(transactionId);

      const key = transactionId ? `transaction:${transactionId}` : `transaction:unknown:${(next += 1)}`;
      sink.push({ tone: "info", title: message, key, duration: Number.POSITIVE_INFINITY });

      if (transactionId) pendingById.set(transactionId, key);
      else unknown = [...unknown, key];
    },
    success: (transactionId: string, message: string) => {
      settle(transactionId);
      sink.push({ tone: "success", title: message });
    },
    error: (transactionId: string, message: string) => {
      settle(transactionId);
      sink.push({ tone: "error", title: message });
    },
  };
};

export type TransactionToasts = ReturnType<typeof createTransactionToasts>;

export const applyTransactionToast = (toasts: TransactionToasts, toast: TransactionToast) => {
  if (toast.stage === "pending") toasts.pending(toast.message, toast.id);
  else if (toast.stage === "success") toasts.success(toast.id, toast.message);
  else toasts.error(toast.id, toast.message);
};
